import React from 'react';
import { THEMES } from '../../../themes/themes';
import { ThemePickerTile } from '../../primitives/ThemePickerTile';
import { VariantTile } from '../../primitives/VariantTile';
import { THEME_VARIANTS } from './constants';

// Nested theme picker modal opened from the forge's Step 1 footer.
// Lives at z-index 230 so it stacks above the CampaignForgeV2 overlay (220).
// Live-preview: every theme/variant click immediately propagates to the
// parent via `update`, so the entire wizard re-renders with the new palette
// while the modal stays open. The footer "Done" button just dismisses.
// Ported from prototypes/campaign-creation-prototype.jsx ThemePickerModal
// (lines 1426-1580); reuses our F.1 ThemePickerTile + VariantTile primitives.
export const ThemeVariantModal = ({ open, onClose, currentTheme, form, update }) => {
  if (!open) return null;
  const c = currentTheme.colors;
  const t = currentTheme.typography || {};

  const selectTheme = (themeKey) => {
    const variants = THEME_VARIANTS[themeKey] || [];
    const firstVariant = variants[0]?.id || '';
    update({ baseTheme: themeKey, baseVariant: firstVariant });
  };

  return (
    <div
      onClick={onClose}
      role="dialog"
      aria-label="Choose a theme"
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.78)',
        backdropFilter: 'blur(2px)',
        WebkitBackdropFilter: 'blur(2px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 230,
        padding: 24,
      }}
    >
      <div
        onClick={(ev) => ev.stopPropagation()}
        style={{
          background: c.bgCardSolid,
          border: `1px solid ${c.borderPrimary}`,
          borderRadius: 14,
          padding: '24px 26px',
          maxWidth: 640,
          width: '100%',
          maxHeight: '88vh',
          overflow: 'auto',
          boxShadow: '0 24px 64px rgba(0,0,0,0.7)',
          position: 'relative',
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
          marginBottom: 16,
        }}>
          <div>
            <div style={{
              fontSize: '0.68rem',
              letterSpacing: '0.22em',
              color: c.accent,
              textTransform: 'uppercase',
              fontWeight: 700,
              fontFamily: t.fontFamily,
              marginBottom: 2,
            }}>Visual theme</div>
            <h2 style={{
              margin: 0,
              fontFamily: t.headingFamily,
              fontSize: '1.3rem',
              fontWeight: 600,
              color: c.accentLight,
              letterSpacing: '0.02em',
            }}>Choose a theme</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'transparent',
              border: 'none',
              color: c.textMuted,
              fontSize: '1.4rem',
              cursor: 'pointer',
              padding: '0 6px',
              lineHeight: 1,
            }}
          >×</button>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: 10,
          marginBottom: 18,
        }}>
          {Object.entries(THEMES)
            .filter(([key]) => THEME_VARIANTS[key])
            .map(([key, themeData]) => (
              <ThemePickerTile
                key={key}
                themeData={themeData}
                active={form.baseTheme === key}
                onClick={() => selectTheme(key)}
                currentTheme={currentTheme}
              />
            ))}
        </div>

        {THEME_VARIANTS[form.baseTheme] && (
          <div>
            <div style={{
              fontSize: '0.7rem',
              letterSpacing: '0.22em',
              color: c.accentLight,
              textTransform: 'uppercase',
              fontWeight: 700,
              marginBottom: 10,
              fontFamily: t.fontFamily,
            }}>Variants</div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
              gap: 8,
            }}>
              {THEME_VARIANTS[form.baseTheme].map((variant) => (
                <VariantTile
                  key={variant.id}
                  variant={variant}
                  active={form.baseVariant === variant.id}
                  onClick={() => update({ baseVariant: variant.id })}
                  currentTheme={currentTheme}
                />
              ))}
            </div>
          </div>
        )}

        <div style={{
          marginTop: 22,
          paddingTop: 16,
          borderTop: `1px solid ${c.borderLight}`,
          display: 'flex', justifyContent: 'flex-end',
        }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: c.accent,
              color: c.textOnAccent,
              border: 'none',
              borderRadius: 6,
              padding: '10px 24px',
              fontFamily: t.fontFamily,
              fontWeight: 600,
              fontSize: '0.9rem',
              cursor: 'pointer',
            }}
          >Done</button>
        </div>
      </div>
    </div>
  );
};
